// The shell's side of offline form fill (round 42): keep the saved forms
// list fresh while there is signal, and warn before a sign-out leaves forms
// behind that have not gone yet.
import { useCallback, useEffect, useState } from 'react';
import { useSession } from '../session';
import { useConfirm } from '../components/ui';
import { offlineStore } from './store';
import { hasUnsent, ownerKey } from './localForm';
import { refreshPack } from './pack';

/**
 * Refreshes the phone's copy of the forms whenever the app opens with signal
 * and whenever the phone comes back online, so the copy a crew member takes
 * into a dead spot is this morning's, not last week's. Silent: failing to
 * refresh leaves the old copy, which is exactly the fallback.
 */
export function useKeepPackFresh(owner: string | null): void {
  useEffect(() => {
    if (!owner) return;
    const refresh = () => void refreshPack(owner).catch(() => undefined);
    refresh();
    window.addEventListener('online', refresh);
    return () => window.removeEventListener('online', refresh);
  }, [owner]);
}

/**
 * Sign out, but say so first when forms on this phone have not reached
 * Dayboard. They are NOT deleted: they are kept against this account and
 * send when it signs back in on this phone. Another person signing in here
 * never sees them.
 */
export function useGuardedSignOut(): () => Promise<void> {
  const { user, tenant, signOut } = useSession();
  const confirm = useConfirm();
  return useCallback(async () => {
    if (user && tenant) {
      const forms = await offlineStore()
        .listForms(ownerKey(tenant.id, user.id))
        .catch(() => []);
      const unsent = forms.filter(hasUnsent).length;
      if (unsent > 0) {
        const sure = await confirm({
          title:
            unsent === 1
              ? 'Sign out with a form not sent yet?'
              : `Sign out with ${unsent} forms not sent yet?`,
          message:
            'They stay on this phone and send when you sign back in here. If you sign in on a different phone, they will not be there.',
          confirmLabel: 'Sign out anyway',
          tone: 'warning',
        });
        if (!sure) return;
      }
    }
    await signOut();
  }, [user, tenant, signOut, confirm]);
}

/**
 * Whether the phone says it is online, kept current. It can say "online"
 * with no working connection (a Wi-Fi hotspot with no internet), so this only
 * ever ADDS reasons to say "No signal"; the session's own unreachable check
 * is the other half.
 */
export function useBrowserOnline(): boolean {
  const [online, setOnline] = useState(() =>
    typeof navigator === 'undefined' ? true : navigator.onLine !== false
  );
  useEffect(() => {
    const up = () => setOnline(true);
    const down = () => setOnline(false);
    window.addEventListener('online', up);
    window.addEventListener('offline', down);
    return () => {
      window.removeEventListener('online', up);
      window.removeEventListener('offline', down);
    };
  }, []);
  return online;
}
